import { Plugin, PluginKey } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";
import { findDueDateSuggestionMatch } from "./findDueDateSuggestionMatch";

/**
 * This decorator plugin provides the visual styling when the due date suggestion
 * experience is active. Decoration should be triggered in the following scenarios
 *
 * 1. If the cursor is immediately after a node of type "mention" as determined
 *    by findDueDateSuggestionMatch. In this case, we want to wrap the
 *    mention node with a decorator and we also want to insert a decoration
 *    widget at the current cursor position. The decoration widget will display
 *    a placeholder for the due date.
 *
 * 2. If the cursor is inside a text node and if that text node is immediately
 *    after a node of type "mention" as determined by findDueDateSuggestionMatch.
 *    Additionally, the text node cannot begin with a space. In this case we want
 *    to wrap the mention node and the text node up to the cursor position with a
 *    decorator node.
 */
export const dueDateDecoratorPlugin = new Plugin({
  key: new PluginKey("dueDateDecorator"),
  state: {
    init: () => DecorationSet.empty,
    apply: (transaction, storage, oldState, newState) => {
      const { doc, selection } = newState;

      // Only apply decorations for text cursor positions
      if (!selection.empty) return DecorationSet.empty;

      const position = selection.$from;

      const match = findDueDateSuggestionMatch({ $position: position });

      if (!match) return DecorationSet.empty;

      const decorations: Decoration[] = [];

      // Decorate the mention node. Note that the DueDateExtension automatically
      // decorates matched query text with a decoration, so we don't need to do that.
      decorations.push(
        Decoration.inline(match.mention.range.from, match.mention.range.to, {
          class: "mention-due-date-suggestion-active",
          nodeName: "span",
        }),
      );

      const isCursorImmediatelyAfterMention = match.range.from === match.range.to;

      if (isCursorImmediatelyAfterMention) {
        // Add a placeholder decoration at the cursor position
        decorations.push(
          Decoration.widget(position.pos, () => {
            const placeholder = document.createElement("span");
            placeholder.className = "due-date-suggestion-placeholder";
            placeholder.textContent = `Try: 2 days, fri, next week`;
            return placeholder;
          }),
        );
      }

      return DecorationSet.create(doc, decorations);
    },
  },
  props: {
    decorations(state) {
      return this.getState(state);
    },
  },
});
